import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { initializeApp, FirebaseApp } from "firebase/app";
import { getFirestore, Firestore } from 'firebase/firestore/lite';
import type { Auth } from 'firebase/auth';
import type { Functions } from 'firebase/functions';
import { environment } from '../../environments/environment';

// Use Firebase config from environment
const firebaseConfig = environment.firebase;

// Environment configuration
const USE_EMULATORS = false; // Set to true for local development

@Injectable({
  providedIn: 'root',
})
export class FirebaseService {
  private platformId = inject(PLATFORM_ID);

  // Firebase instances
  public app: FirebaseApp;
  public db: Firestore;

  // Auth and Functions are loaded lazily (see getAuth/getFunctions). The
  // public-facing product page (QR scan) only needs Firestore, so we keep
  // the heavy `firebase/auth` (~140 kB) and `firebase/functions` modules out
  // of the initial bundle and load them on demand for the admin/login flows.
  public auth: Auth | null = null;
  public functions: Functions | null = null;
  private authPromise: Promise<Auth | null> | null = null;
  private functionsPromise: Promise<Functions | null> | null = null;

  constructor() {
    // Initialize Firebase app
    this.app = initializeApp(firebaseConfig);

    // Initialize Firestore (lightweight, needed everywhere)
    this.db = getFirestore(this.app);
  }

  /**
   * Lazily initialize Firebase Auth. Dynamically imports `firebase/auth` so it
   * is code-split out of the initial bundle. Returns null outside the browser.
   * The resolved instance is cached on `this.auth` for synchronous reuse.
   */
  async getAuth(): Promise<Auth | null> {
    if (!this.isBrowser) return null;
    if (this.auth) return this.auth;
    if (!this.authPromise) {
      this.authPromise = import('firebase/auth').then(({ getAuth, connectAuthEmulator }) => {
        const auth = getAuth(this.app);
        if (USE_EMULATORS) {
          connectAuthEmulator(auth, 'http://localhost:9099', { disableWarnings: true });
        }
        this.auth = auth;
        return auth;
      });
    }
    return this.authPromise;
  }

  /**
   * Lazily initialize Firebase Functions. Dynamically imports
   * `firebase/functions` so it is code-split out of the initial bundle.
   * Returns null outside the browser.
   */
  async getFunctions(): Promise<Functions | null> {
    if (!this.isBrowser) return null;
    if (this.functions) return this.functions;
    if (!this.functionsPromise) {
      this.functionsPromise = import('firebase/functions').then(({ getFunctions, connectFunctionsEmulator }) => {
        const functions = getFunctions(this.app, 'us-central1');
        if (USE_EMULATORS) {
          connectFunctionsEmulator(functions, 'localhost', 5001);
        }
        this.functions = functions;
        return functions;
      });
    }
    return this.functionsPromise;
  }

  /**
   * Check if running in browser environment
   */
  get isBrowser(): boolean {
    return isPlatformBrowser(this.platformId);
  }
}
